import { UseFormReturn } from 'react-hook-form';
import { useIntl } from 'react-intl';

import InputFormField from 'src/registry/components/forms/InputFormField';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import TextareaFormField from 'src/registry/components/forms/TextareaFormField';
import { Form, FormDescription } from 'src/registry/ui/Form';
import { commonMessages, generalMessages } from 'src/utils/messages';
import { BOOLEAN_MAP, BOOLEAN_OPTIONS, CURRENCY_MAP } from 'src/features/moneyAccounts/utils/constants';
import { CURRENCY } from 'src/types';
import { ReferenceRateFormSchemaValues } from 'src/features/referenceRates/utils/formSchema';
import { referenceRatesMessages } from 'src/features/referenceRates/utils/messages';
import { REFERENCE_RATE_UNIT } from 'src/features/referenceRates/utils/types';
import { REFERENCE_RATE_UNIT_MAP } from 'src/features/referenceRates/utils/constants';

type ReferenceRateFormProps = {
    form: UseFormReturn<ReferenceRateFormSchemaValues>;
    onSubmit: (values: ReferenceRateFormSchemaValues) => void;
};

function ReferenceRateForm({ onSubmit, form }: ReferenceRateFormProps) {
    const { formatMessage } = useIntl();
    const unit = form.watch('unit');

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <InputFormField<ReferenceRateFormSchemaValues>
                form={form}
                name="name"
                label={formatMessage(commonMessages.name)}
            />
            <InputFormField<ReferenceRateFormSchemaValues>
                form={form}
                name="code"
                label={formatMessage(referenceRatesMessages.code)}
            />
            <SelectFormField<ReferenceRateFormSchemaValues>
                form={form}
                name="unit"
                label={formatMessage(referenceRatesMessages.unit)}
                options={Object.values(REFERENCE_RATE_UNIT)}
                optionTranslationMap={REFERENCE_RATE_UNIT_MAP}
            />
            {/* Like the currency below, the unit locks once values exist - warn, and let a 400 land on the field. */}
            <FormDescription>{formatMessage(referenceRatesMessages.unitHint)}</FormDescription>
            {unit === REFERENCE_RATE_UNIT.CURRENCY && (
                <>
                    <SelectFormField<ReferenceRateFormSchemaValues>
                        form={form}
                        name="currency"
                        label={formatMessage(commonMessages.currency)}
                        options={Object.values(CURRENCY)}
                        optionTranslationMap={CURRENCY_MAP}
                    />
                    {/*
                     * The backend refuses a currency change once a pricing policy indexes the rate, and the
                     * list response gives us no way to know whether that has happened - so warn rather than
                     * disable, and let the server error land on the field.
                     */}
                    <FormDescription>{formatMessage(referenceRatesMessages.currencyLockedHint)}</FormDescription>
                </>
            )}
            <TextareaFormField<ReferenceRateFormSchemaValues>
                form={form}
                name="description"
                required={false}
                label={formatMessage(generalMessages.description)}
            />
            <SelectFormField<ReferenceRateFormSchemaValues>
                form={form}
                name="is_active"
                isBoolean={true}
                required={false}
                label={formatMessage(referenceRatesMessages.isActive)}
                options={Object.values(BOOLEAN_OPTIONS)}
                optionTranslationMap={BOOLEAN_MAP}
            />
        </Form>
    );
}

export default ReferenceRateForm;
